import { DriveFileRenameOutlineRounded as DriveFileRenameOutlineRoundedIcon, MoreVert as MoreVertIcon } from '@mui/icons-material'
import { alpha, Card, CardActionArea, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, styled, Typography, Box, Stack } from '@mui/material'
import { type ChangeEvent, type MouseEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import type { UserImageStore } from '../../UserData/_stores/UserImageStore'
import DeleteIcon from '../../Common/Icons/DeleteIcon'
import RenameImageDialog from './RenameImageDialog'
import DeleteImageDialog from './DeleteImageDialog'
import { formatImageFileSize, formatImageFileType } from './imageFileMetadata'
import usePreventShiftTextSelection, { hasShiftKey, isInteractiveEventTarget } from '../../Common/_hooks/usePreventShiftTextSelection'
import SelectionCheckbox from '../SelectionCheckbox'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import SizeDisplay, { formatMeasurementNumber } from '../../Common/Measurements/SizeDisplay'
import { getValidImageDpi } from '../../Common/Measurements/imageResolution'

export interface Props {
  item: UserImageStore
  onSelect?: (item: UserImageStore) => void
  selected?: boolean
  highlighted?: boolean
  onSelectImage?: (id: number, shiftKey?: boolean) => void
  onUnselectImage?: (id: number, shiftKey?: boolean) => void
}

export const ImageCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  outline: '2px solid transparent',
  transition: 'outline 150ms, background-color 150ms, box-shadow 150ms',

  '&.selected': {
    outline: '2px solid ' + theme.palette.primary.main,
  },

  '&.clickable': {
    cursor: 'pointer',

    'img': {
      userSelect: 'none',
    },

    '&:hover': {
      outline: '5px solid ' + alpha(theme.palette.primary.main, 0.15),

      '&.selected': {
        outline: '5px solid ' + theme.palette.primary.main,
      },
    },
  },

  '&.highlighted': {
    outline: '2px solid ' + theme.palette.accent.main,
    boxShadow: '0 0 0 5px ' + alpha(theme.palette.accent.main, 0.18),
  },

  '&.highlighted.selected': {
    outline: '2px solid ' + theme.palette.primary.main,
    boxShadow: 'none',
  },
}))

export const imageCardGridProps = {
  xs: 6,
  sm: 4,
  lg: 3,
  xl: 2,
} as const

export const imagePreviewSx = {
  paddingBottom: {
    xs: '60%',
    sm: '90%',
  },
  height: 0,
  position: 'relative',
} as const

export default function Image(props: Props) {
  const [t] = useTranslation('account-v2')

  const [menuOpen, setMenuOpen] = useState(false)
  const [menuRenameOpen, setMenuRenameOpen] = useState(false)
  const [menuDeleteOpen, setMenuDeleteOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const onMenuOpen = useCallback(() => {
    setMenuOpen(true)
  }, [])
  const onMenuClose = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const onMenuRenameOpen = useCallback(() => {
    setMenuOpen(false)
    setMenuRenameOpen(true)
  }, [])

  const onMenuRenameClose = useCallback(() => {
    setMenuRenameOpen(false)
  }, [])

  const onMenuDeleteOpen = useCallback(() => {
    setMenuOpen(false)
    setMenuDeleteOpen(true)
  }, [])

  const onMenuDeleteClose = useCallback(() => {
    setMenuDeleteOpen(false)
  }, [])

  const hasSelection = !!props.onSelectImage && !!props.onUnselectImage
  const imageId = Number(props.item.get('id'))
  const isSelectedFromStore = useAppSelector(state => {
    const account = state.get('UIData').get('account')

    if (account.get('imageSelectedAllImages')) {
      return !account.get('imageExcludedSelectedImages').has(imageId)
    }

    return account.get('imageSelectedImages').has(imageId)
  })
  const isSelected = props.selected ?? isSelectedFromStore
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))
  const dpi = getValidImageDpi(props.item.get('dpi'))
  const imageDimensions = useMemo((): number[] | undefined => {
    const width = Number(props.item.get('width'))
    const height = Number(props.item.get('height'))
    return width > 0 && height > 0 ? [width, height] : undefined
  }, [props.item]);

  const imageMetadata = useMemo(() => {
    const fileType = formatImageFileType(props.item.get('original_format'), props.item.get('original_filename') || props.item.get('name'))
    const filesizeValue = Number(props.item.get('filesize'))
    const filesize = filesizeValue > 0 ? formatImageFileSize(filesizeValue) : undefined


    return [fileType, filesize].filter(Boolean).join(' • ')
  }, [props.item])

  const onChangeCheckbox = useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    e.stopPropagation()

    if (!props.onSelectImage || !props.onUnselectImage) return

    const shiftKey = hasShiftKey(e.nativeEvent)

    if (checked) {
      props.onSelectImage(imageId, shiftKey)
    } else {
      props.onUnselectImage(imageId, shiftKey)
    }
  }, [imageId, props.onSelectImage, props.onUnselectImage])

  const onToggleSelection = useCallback((shiftKey = false) => {
    if (!props.onSelectImage || !props.onUnselectImage) return

    if (!isSelected) {
      props.onSelectImage(imageId, shiftKey)
    } else {
      props.onUnselectImage(imageId, shiftKey)
    }
  }, [imageId, isSelected, props.onSelectImage, props.onUnselectImage])

  const onCardClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!hasSelection) return

    if (isInteractiveEventTarget(e.target)) {
      return
    }

    onToggleSelection(e.shiftKey)
  }, [hasSelection, onToggleSelection])

  const onCardMouseDown = usePreventShiftTextSelection<HTMLDivElement>(hasSelection)

  const onClick = useMemo(() => {
    const onSelect = props.onSelect

    if (!onSelect || hasSelection) return undefined

    return () => {
      onSelect(props.item)
    }
  }, [hasSelection, props.onSelect, props.item])

  const imageElem = useMemo(() => {
    return <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <img src={props.item.get('filename')} style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none'
      }} />
    </div>
  }, [props.item])

  return <Grid item key={props.item.get('id')} {...imageCardGridProps}>
    <ImageCard
      elevation={3}
      className={classNames({
        selected: isSelected,
        highlighted: props.highlighted,
        clickable: hasSelection,
      })}
      onClick={onCardClick}
      onMouseDown={onCardMouseDown}
    >
      {hasSelection ? <SelectionCheckbox
        checked={isSelected}
        onChange={onChangeCheckbox}
        sx={(theme) => ({
          left: theme.spacing(0.5),
          position: 'absolute',
          top: theme.spacing(0.5),
          zIndex: 1,
        })}
      /> : null}
      {onClick ? <CardActionArea
        onClick={onClick}
        sx={imagePreviewSx}
      >{imageElem}</CardActionArea> : <Box
        component="div"
        sx={imagePreviewSx}
      >{imageElem}</Box>}

      <Stack sx={{
        px: 2,
        pt: 1,
        backgroundColor: '#fff',
      }}>
        <Typography
          variant="h4"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          title={props.item.get('name')}
        >{props.item.get('name')}</Typography>
        <Stack direction="row" sx={{alignItems: 'center'}}>
          <Stack
            sx={{
              flexGrow: 1,
              minWidth: 0,
              pb: 1,
            }}
          >
            {imageDimensions ? <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                lineHeight: 1.35,
              }}
            >
              <SizeDisplay sizes={imageDimensions} sourceUnit="px" displayType="digital" />
            </Typography> : null}
            {imageDimensions && dpi !== undefined ? <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.35 }}
            >
              <SizeDisplay sizes={imageDimensions} sourceUnit="px" displayType="physical" dpi={dpi} />
              {' '}{t('@ {{dpi}} DPI', { dpi: formatMeasurementNumber(dpi, locale, 2) })}
            </Typography> : null}
            {imageMetadata ? <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                lineHeight: 1.35,
              }}
            >{imageMetadata}</Typography> : null}
          </Stack>
          <IconButton
            onClick={onMenuOpen}
            ref={setAnchorEl}
            size="large"
            sx={{
              flexShrink: 0,
              mt: -1,
              mr: -2,
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Menu
        onClose={onMenuClose}
        open={menuOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem onClick={onMenuRenameOpen}>
          <ListItemIcon><DriveFileRenameOutlineRoundedIcon /></ListItemIcon>
          <ListItemText>{t('Rename')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={onMenuDeleteOpen}
          sx={{
            color: 'error.main',
            '& .MuiListItemIcon-root': {
              color: 'error.main',
            },
          }}
        >
          <ListItemIcon><DeleteIcon /></ListItemIcon>
          <ListItemText>{t('Delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </ImageCard>

    <RenameImageDialog
      image={props.item}
      opened={menuRenameOpen}
      onClose={onMenuRenameClose}
    />

    <DeleteImageDialog
      image={props.item}
      opened={menuDeleteOpen}
      onClose={onMenuDeleteClose}
    />
  </Grid>
}
